import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Trash2 } from "lucide-react";

const EVENT_COLOR = {
  play: "text-emerald-400",
  ad_start: "text-amber-400",
  ad_end: "text-amber-400",
  scte35: "text-red-400",
  graphic_on: "text-blue-400",
  graphic_off: "text-blue-400",
  failover: "text-red-500",
  error: "text-red-500",
  stop: "text-zinc-400",
};

function PlayoutEvents({ channels }) {
  const [channelId, setChannelId] = useState("all");
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    const params = { limit: 500 };
    if (channelId !== "all") params.channel_id = channelId;
    api.get("/as-run", { params }).then((r) => setLogs(r.data));
  }, [channelId]);
  return (
    <>
      <div className="flex justify-end mb-3">
        <Select value={channelId} onValueChange={setChannelId}>
          <SelectTrigger className="w-56 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="asrun-channel-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all">All channels</SelectItem>
            {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="panel">
        <div className="max-h-[70vh] overflow-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-950 z-10">
              <tr className="text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-3 py-2 w-56">Timestamp</th>
                <th className="text-left px-3 py-2 w-32">Event</th>
                <th className="text-left px-3 py-2 w-40">Channel</th>
                <th className="text-left px-3 py-2">Message</th>
                <th className="text-right px-3 py-2 w-24">Dur (s)</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {logs.map((l) => {
                const ch = channels.find((c) => c.id === l.channel_id);
                return (
                  <tr key={l.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/40" data-testid={`asrun-row-${l.id}`}>
                    <td className="px-3 py-1.5 text-zinc-400">{new Date(l.event_time).toISOString().slice(0, 19).replace("T", " ")}</td>
                    <td className={`px-3 py-1.5 uppercase ${EVENT_COLOR[l.event_type] || "text-zinc-400"}`}>{l.event_type}</td>
                    <td className="px-3 py-1.5 text-zinc-300">{ch?.name || l.channel_id.slice(0, 6)}</td>
                    <td className="px-3 py-1.5 text-zinc-500">{l.asset_name || l.message}</td>
                    <td className="px-3 py-1.5 text-right text-zinc-500">{l.duration_sec?.toFixed?.(0) || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function DeletionsLog() {
  const [items, setItems] = useState([]);
  const [actor, setActor] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (actor) params.actor = actor;
    if (q) params.q = q;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/assets/delete-log", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fmtWhen = (iso) => { try { return new Date(iso).toISOString().slice(0, 19).replace("T", " "); } catch { return iso; } };

  return (
    <>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Actor</label>
          <Input data-testid="deletelog-actor" value={actor} onChange={(e) => setActor(e.target.value)} placeholder="email or name" className="h-8 w-48 bg-zinc-950 border-zinc-800 rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Asset / reason</label>
          <Input data-testid="deletelog-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search…" className="h-8 w-48 bg-zinc-950 border-zinc-800 rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">From</label>
          <Input data-testid="deletelog-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40 bg-zinc-950 border-zinc-800 rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">To</label>
          <Input data-testid="deletelog-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40 bg-zinc-950 border-zinc-800 rounded-sm text-sm" />
        </div>
        <Button data-testid="deletelog-apply" onClick={load} className="h-8 tenant-btn rounded-sm text-xs"><Search className="w-3 h-3 mr-1" /> Apply</Button>
        {(actor || q || from || to) && (
          <Button data-testid="deletelog-clear" variant="outline" onClick={() => { setActor(""); setQ(""); setFrom(""); setTo(""); setTimeout(load, 0); }} className="h-8 rounded-sm text-xs">Clear</Button>
        )}
      </div>
      <div className="panel">
        <div className="max-h-[70vh] overflow-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-950 z-10">
              <tr className="text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-3 py-2 w-52">When</th>
                <th className="text-left px-3 py-2 w-56">Actor</th>
                <th className="text-left px-3 py-2 w-16 text-right">Count</th>
                <th className="text-left px-3 py-2 w-48">Reason</th>
                <th className="text-left px-3 py-2">Assets</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-500" data-testid="deletelog-empty">No deletions match these filters.</td></tr>
              ) : (
                items.map((e, i) => (
                  <tr key={i} className="border-b border-zinc-900/60 hover:bg-zinc-900/40" data-testid={`deletelog-row-${i}`}>
                    <td className="px-3 py-1.5 text-zinc-400">{fmtWhen(e.at)}</td>
                    <td className="px-3 py-1.5 text-zinc-300">{e.actor_email}{e.actor_name ? <span className="text-zinc-500"> · {e.actor_name}</span> : null}</td>
                    <td className="px-3 py-1.5 text-right text-rose-400 font-bold">{e.extras?.count || 1}</td>
                    <td className="px-3 py-1.5 text-zinc-400">{e.extras?.reason || "—"}</td>
                    <td className="px-3 py-1.5 text-zinc-500 truncate max-w-[26rem]" title={(e.extras?.assets || []).map((a) => a.name).join(", ")}>
                      {(e.extras?.assets || []).map((a) => a.name).join(", ")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function AsRunLogs() {
  const [channels, setChannels] = useState([]);
  const [tab, setTab] = useState("events");
  useEffect(() => { api.get("/channels").then((r) => setChannels(r.data)); }, []);

  return (
    <div className="p-4">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ As-Run Logs</div>
          <h1 className="font-display font-bold text-2xl mt-1">Immutable event log</h1>
        </div>
      </div>
      <div className="flex items-center gap-2 border-b border-zinc-800 mb-4">
        <button data-testid="asrun-tab-events" onClick={() => setTab("events")} className={`text-xs font-mono uppercase tracking-widest px-3 py-2 -mb-px border-b-2 ${tab === "events" ? "border-cyan-400 text-cyan-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>Playout events</button>
        <button data-testid="asrun-tab-deletions" onClick={() => setTab("deletions")} className={`text-xs font-mono uppercase tracking-widest px-3 py-2 -mb-px border-b-2 flex items-center gap-1 ${tab === "deletions" ? "border-rose-400 text-rose-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}><Trash2 className="w-3 h-3" /> Deletions</button>
      </div>
      {tab === "events" ? <PlayoutEvents channels={channels} /> : <DeletionsLog />}
    </div>
  );
}
